import {
  Calendar,
  Day,
  utils
} from '@amir04lm26/react-modern-calendar-date-picker'
import download from 'downloadjs'
import { t } from 'i18next'
import set from 'lodash/set'
import { DateTime } from 'luxon'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState
} from 'react'
import { RouteComponentProps, StaticContext } from 'react-router'
import { Tooltip } from 'react-tippy'
import { $enum } from 'ts-enum-util'

import { DiscontinueConfirmationModal } from './DiscontinueConfirmationModal'
import { useDiscontinueConfirmationModal } from './DiscontinueConfirmationModal/DiscontinueConfirmationModal.model'
import { DiscontinueWarningModal } from './DiscontinueWarningModal'
import { PatientDetail } from './PatientDetail'
import {
  getNumberOfVialsToReconcile,
  useAugmentedOrders,
  useProgram
} from './PatientDetail.model'
import {
  StyledCalendarModal,
  StyledCalendarWrapper
} from './PatientDetail.styles'
import {
  ICalendarPicker,
  IPastOrderSummary,
  IPatientVial,
  VialReconciliationType
} from './PatientDetail.types'
import { StyledStatusWrapper } from './PatientDetailCard.styles'
import { PatientAccessFormItem } from './PatientDetailContainer.styles'
import { PatientDetailDiscontinued } from './PatientDetailDiscontinued/PatientDetailDiscontinued'
import { ITransferForm } from './TransferPatientForm/TransferPatientForm'
import { ClinTheme } from '../../../ClinTheme'
import {
  AnnounceMode,
  ClinAnnounceBar
} from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinButton } from '../../../components/ClinButton'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import { ClinNotification } from '../../../components/ClinNotification'
import { IPagination } from '../../../components/ClinPagination/ClinPagination.model'
import { SortDirectionType } from '../../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import { ClinText } from '../../../components/ClinText'
import { PatientDocumentType } from '../../../constants'
import { ProgramCountryStatus } from '../../../constants/program'
import { useAppContext } from '../../../context/app'
import {
  clearAnnounceEvent,
  createAnnounceEvent
} from '../../../events/AnnounceEvent'
import { StyledDocumentIcon } from '../../../features/DocumentUpload/DocumentUpload.styles'
import useChangeBackgroundColor from '../../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import { useEffectOnlyOnce } from '../../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import { AnalyticsEvent } from '../../../services/Analytics'
import { AnalyticsPageEvent } from '../../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import {
  downloadDocumentById,
  reconcileVialForPatient,
  getReadOnlyOpaUrl,
  cancelGetReadOnlyOpaUrl
} from '../../../services/ApiService'
import {
  pushAccessFormWhenReady,
  pushCheckoutWhenReady
} from '../../../services/pushAccessFormWhenReady'
import { findPatientSummaryByNumber } from '../../../services/findPatientSummaryByNumber'
import { resolveCheckoutOrderNumber } from '../../../services/findDraftOrderNumber'
import { IRestEndpointState } from '../../../types/IRestEndpointState'
import { OrderStatus } from '../../../types/OrderStatus'
import {
  InstituteDto,
  PatientDetailsDto,
  ProgramDocumentDto,
  ShortOrderSummaryDto
} from '../../../types/swaggerTypes'
import {
  convertDateStringToDay,
  convertDayToISODateString
} from '../../../utils/dateUtils'
import { getBrowserLocale } from '../../../utils/getBrowserLocale'
import { getFilenameFromUrl } from '../../../utils/getFilenameFromUrl'
import { openCastorEdcUrl } from '../../../utils/rwdFollowUpEligibility'
import {
  resolveRwdNavContextForPatientDetailPage,
  shouldDeferPatientRwdNavUpdate
} from '../../../utils/rwdNavVisibility'
import {
  shouldRestrictInitialOrder,
  shouldRestrictResupplyOrder
} from '../../../utils/programRestrictions'
import { useOnMountWithSubsequent } from '../../../utils/useOnMountWithSubsequent'
import {
  TabModel,
  getShowRWDCard
} from '../../Programs/ProgramDetail/ProgramDetail.models'
import {
  PatientOrderStatus,
  OPAOrderTypes,
  ActionDropdownConstants,
  patientOrderStatusTooltipTranslation,
  patientOrderStatusTranslation,
  normalizePatientOrderStatus
} from '../PatientDashboard/Patient.model'
import { isPatientDetailPostTrialAccess } from '../patientInitialsDisplay'
import {
  isPtaConsentSyncPending,
  shouldDisablePtaBeginOrder
} from './ptaBeginOrderGate'
import {
  isPtaPatientTransferBlocked,
  redirectToPatientDetail
} from './ptaTransferGate'
import {
  PTA_BASELINE_CAPTURED_SUCCESS_MESSAGE,
  shouldClearPtaBaselineError
} from './ptaConsentBaselineCompleteAnnounce'
import { screeningFormsRefusalAction } from '../ptaScreeningFormsLink'
import { usePtaConsentBaselineCompleteAnnounce } from './usePtaConsentBaselineCompleteAnnounce'
import { usePtaPatientDetailsRefreshOnReturn } from './usePtaPatientDetailsRefreshOnReturn'
import StatusTooltip from '../PatientDashboard/StatusTooltip'
import { MissingDraftOrderModal } from '../MissingDraftOrderModal/MissingDraftOrderModal'
import StyledTrackingLinkWrapper from '../PatientDashboard/StyledTrackingLinkWrapper'
import { PastOrderColumn } from '../Patients.model'
import { ClinSpinner } from '../../../components/ClinSpinner'

/**
 * Helper function to check if an order should be restricted based on program status
 * @param program The program data with country status
 * @param patient The patient details
 * @param orderType The type of order (initial, stock, resupply)
 * @param dispatch The dispatch function for showing error messages
 * @returns true if the navigation should be prevented, false otherwise
 */
const shouldRestrictThisOrder = (
  program: any,
  patient: PatientDetailsDto,
  orderType: OPAOrderTypes,
  dispatch: any
): boolean => {
  if (!program || !program.program) {
    return false
  }

  const programStatus = program.programCountry
    ?.countryProgramStatus as ProgramCountryStatus
  const isStockOrder = patient.stockOrderFlag === 'Y'

  // Check if this is an initial order that should be restricted
  if (
    !isStockOrder && // Not a stock order
    shouldRestrictInitialOrder(
      programStatus,
      patient.patientOrderStatus as PatientOrderStatus,
      orderType
    )
  ) {
    // Show error message
    dispatch(
      createAnnounceEvent(
        AnnounceMode.Error,
        t('patient_access_form:program_resupply_only_error'),
        t('patient_access_form:program_resupply_only_error_title')
      )
    )
    return true
  }

  // Check if this is a resupply order that should be restricted
  if (shouldRestrictResupplyOrder(programStatus, orderType, isStockOrder)) {
    // Show error message for stock orders only
    dispatch(
      createAnnounceEvent(
        AnnounceMode.Error,
        t('patient_access_form:program_stock_only_error'),
        t('patient_access_form:program_stock_only_error_title')
      )
    )
    return true
  }

  return false
}

export enum ReconcileStatus {
  SuccessfulReconciliation,
  RequiresReconciliation,
  NoReconciliationRequired
}

export interface IPastOrderColumn {
  title: string
  renderValue: (
    order: ShortOrderSummaryDto,
    index?: number
  ) => JSX.Element | string
  width: string
  hide?: boolean
  isSortable?: boolean
  name?: string
}

const defaultPagination: IPagination = {
  count: 0,
  skip: 0,
  take: 5,
  total: 0
}

type LocationState = {
  from?: string
  endpoint?: string
  orderNumber?: string
  activeTabIndex?: number
}

interface IPatientDetailRouteParams {
  patientId: string
  physicianId: string
}

interface IPatientDetailProps
  extends RouteComponentProps<
    IPatientDetailRouteParams,
    StaticContext,
    LocationState
  > {}

/** CLOS-13517: hardcoded until Squidex/i18n keys are added. */
const PTA_BEGIN_ORDER_ACTION_LABEL = 'Begin order'

/**
 * Width that breaks the PTA success message into two lines after "...submitted you"
 * (14 px HCo Gotham, 0.4 px letter spacing: line one 407 px, "...you may" 443 px), so the
 * message area is set mid-way at 425 px plus the icon, divider, Begin order and close.
 */
const PTA_SUCCESS_NOTIFICATION_MAX_WIDTH = 644

export const PatientDetailContainer: FunctionComponent<IPatientDetailProps> = ({
  match,
  history,
  location
}) => {
  //region useState and useEffect
  const {
    dispatch,
    institute,
    defaultShippingAddress,
    portfolioCountryCode,
    setRwdNavProgramContext,
    user,
    userDetails
  } = useAppContext()
  const { patientId, physicianId } = match.params
  const [pagination, setPagination] = useState<IPagination>(defaultPagination)
  const [hasReconciliation, setHasReconciliation] = useState<boolean>(false)
  const [detailsCtaButtonTitle, setDetailsCtaButtonTitle] = useState<string>('')
  const [orderNumber, setOrderNumber] = useState<string>('')
  const [isPatientOrderNumber, setIsPatientOrderNumber] =
    useState<boolean>(false)
  const [isLookingUpDraftOrder, setIsLookingUpDraftOrder] = useState(false)

  const patientCentricFeatureFlag = true

  useChangeBackgroundColor(ClinTheme.colors.lightGrey)

  useOnMountWithSubsequent(() => {
    if (history.location?.state?.orderNumber) {
      setOrderNumber(history.location?.state?.orderNumber)
    }
  })

  const {
    isLoading,
    patient,
    augmentedOrders,
    isProgramClosed,
    allowsDiscontinuation,
    getPatient
  } = useAugmentedOrders(
    patientId,
    physicianId,
    setPagination,
    setHasReconciliation,
    dispatch
  )
  const [vialsToReconcileCounter, setVialsToReconcileCounter] =
    useState<number>(0)

  const { isProgramLoading, program } = useProgram(
    patient?.program?.programId ?? undefined
  )

  const supportsReconcilliation = program?.program?.supportsReconcilliation
  const showRecordTreatmentTab =
    supportsReconcilliation === 'Yes' || supportsReconcilliation === 'Y'

  const isPostTrialAccess = isPtaPatientTransferBlocked(
    patient?.program?.status,
    program,
    defaultShippingAddress,
    portfolioCountryCode
  )

  useEffect(() => {
    const deferUpdate = shouldDeferPatientRwdNavUpdate({
      patient,
      program,
      isPatientLoading: isLoading,
      isProgramLoading
    })

    if (deferUpdate) {
      return
    }

    setRwdNavProgramContext(
      resolveRwdNavContextForPatientDetailPage(
        patient,
        program,
        portfolioCountryCode
      ),
      'page'
    )
  }, [
    patient,
    program,
    portfolioCountryCode,
    setRwdNavProgramContext,
    isLoading,
    isProgramLoading
  ])

  const [baselineErrorMessage, setBaselineErrorMessage] = useState<
    string | undefined
  >()

  const dismissBaselineError = useCallback(
    () => setBaselineErrorMessage(undefined),
    []
  )

  useEffect(() => {
    setBaselineErrorMessage(undefined)
  }, [patientId, physicianId])

  const {
    showConsentBaselineCompleteNotification,
    dismissConsentBaselineCompleteNotification,
    confirmConsentBaselineComplete,
    isCheckingConsentBaselineProgress,
    isConsentBaselineConfirmed
  } = usePtaConsentBaselineCompleteAnnounce({
    patientId,
    physicianId,
    patient,
    programCatalog: program,
    defaultShippingAddress,
    instituteCountryCode: portfolioCountryCode,
    isLoading: isLoading || isProgramLoading,
    getPatient,
    onProgressCheckFailed: setBaselineErrorMessage,
    onProgressCheckIncomplete: setBaselineErrorMessage
  })

  // The confirmation box sits low on the page; bring the user back to the top so the
  // success notification and the header's Begin order are in view together.
  useEffect(() => {
    if (showConsentBaselineCompleteNotification) {
      window.scrollTo({ behavior: 'smooth', top: 0 })
    }
  }, [showConsentBaselineCompleteNotification])

  const handleConfirmConsentBaseline = useCallback(() => {
    setBaselineErrorMessage(undefined)
    return confirmConsentBaselineComplete()
  }, [confirmConsentBaselineComplete])

  const shouldClearBaselineError = shouldClearPtaBaselineError(
    patient,
    isConsentBaselineConfirmed
  )

  useEffect(() => {
    if (shouldClearBaselineError) {
      setBaselineErrorMessage(undefined)
    }
  }, [shouldClearBaselineError])

  usePtaPatientDetailsRefreshOnReturn({
    isPostTrialAccess,
    isLoading: isLoading || isProgramLoading,
    getPatient,
    isConsentSyncPending: isPtaConsentSyncPending(patient, isPostTrialAccess),
    syncKey: `${physicianId}:${patientId}`
  })

  const {
    isConfirmationOpen,
    isConfirmationSubmitting,
    isConfirmationSubmitted,
    confirmationError,
    handleOpenConfirmation,
    handleCloseConfirmation,
    handleClearSubmission
  } = useDiscontinueConfirmationModal(
    patientId,
    physicianId,
    institute.data?.instituteId
  )

  const getUrlParams = useCallback(() => {
    const urlSearchParams = new URLSearchParams(history.location.search)
    const wantsTransferForm = Boolean(
      urlSearchParams.get('transfer-patient-form')
    )

    if (!wantsTransferForm) {
      setIsFormOpen(false)
      return
    }

    if (isProgramLoading || !patient) {
      setIsFormOpen(false)
      return
    }

    if (isPostTrialAccess) {
      setIsFormOpen(false)
      redirectToPatientDetail(history, physicianId, patientId)
      return
    }

    setIsFormOpen(true)
  }, [
    history,
    physicianId,
    patientId,
    patient,
    isProgramLoading,
    isPostTrialAccess,
    history.location.search
  ])

  const [pastOrderSortConfig, setPastOrderSortConfig] = useState({
    sortColumn: '',
    sortOrder: SortDirectionType.None
  })

  // Cleanup any pending API calls when component unmounts
  useEffect(() => {
    return () => {
      cancelGetReadOnlyOpaUrl()
    }
  }, [])

  // Update orders when received and augmented
  useEffect(() => {
    const allPastOrdersClone = augmentedOrders.slice()

    const sortedPastOrders = sortPastOrders(
      allPastOrdersClone,
      pastOrderSortConfig.sortColumn as string,
      pastOrderSortConfig.sortOrder
    )

    setPastOrders([
      ...sortedPastOrders.splice(pagination.skip, pagination.take)
    ])

    getUrlParams()
    const unListen = history.listen(() => {
      getUrlParams()
    })
    return () => {
      unListen()
    }
  }, [
    augmentedOrders,
    getUrlParams,
    history,
    pagination.take,
    pastOrderSortConfig
  ])
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [isReconcileWarningOpen, setIsReconcileWarningOpen] =
    useState<boolean>(false)

  const [pastOrders, setPastOrders] = useState<IPastOrderSummary[]>([])

  // Set initial tabIndex based on pastOrdersFiltered
  const [tabIndex, setTabIndex] = useState<number>(
    typeof location?.state?.activeTabIndex === 'number'
      ? location?.state?.activeTabIndex
      : 0
  )

  // Selected row logic
  const [selectedVialRow, setSelectedVialRow] = useState<
    IPatientVial | undefined
  >()
  const [showCalendar, setShowCalendar] = useState<ICalendarPicker>({
    isOpen: false,
    minimumDate: utils('en').getToday(),
    selectedDay: utils('en').getToday(),
    maximumDate: utils('en').getToday()
  })
  const [isSavingVial, setIsSavingVial] = useState<boolean>(false)

  const [isTransferringAnotherInstitute, setIsTransferringAnotherInstitute] =
    useState(false)

  const [isLoadingOrderOpaUrl, setIsLoadingOrderOpaUrl] = useState<
    string | null
  >(null)
  const [orderOpaUrls, setOrderOpaUrls] = useState<Record<string, string>>({})

  //endregion

  useEffect(() => {
    setDetailsCtaButtonTitleBasedOnStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, isConsentBaselineConfirmed])

  const allStatusActions = {
    [ActionDropdownConstants.BeginOrder]: {
      text: 'patient_detail:patient_actions.begin_order'
    },
    [ActionDropdownConstants.DiscontinuePatient]: {
      text: 'patient_detail:patient_actions.discontinue_patients'
    },
    [ActionDropdownConstants.ContinueOrder]: {
      text: 'patient_detail:patient_actions.continue_order'
    },
    [ActionDropdownConstants.OrderResuply]: {
      text: 'patient_detail:patient_actions.order_resupply'
    },
    [ActionDropdownConstants.TrackYourOrder]: {
      text: 'patient_detail:patient_actions.track_your_order'
    },
    [ActionDropdownConstants.ReconcileVials]: {
      text: 'patient_detail:patient_actions.reconcile_vials'
    },
    [ActionDropdownConstants.TransferPatient]: {
      text: 'patient_detail:patient_actions.transfer_patients'
    }
  }

  const announcePtaOrderBlocked = () => {
    dispatch(
      createAnnounceEvent(
        AnnounceMode.Error,
        'Please complete patient consent and screening information before placing an order.',
        'Order not available'
      )
    )
  }

  const isPtaOrderBlocked = (targetPatient: PatientDetailsDto): boolean => {
    const isPtaPatient = isPatientDetailPostTrialAccess(
      targetPatient.program?.status,
      program,
      defaultShippingAddress,
      portfolioCountryCode
    )

    return shouldDisablePtaBeginOrder(
      targetPatient,
      isPtaPatient,
      isConsentBaselineConfirmed
    )
  }

  const setDetailsCtaButtonTitleBasedOnStatus = () => {
    if (!patient) return

    const stockOrderFlag = patient?.stockOrderFlag === 'Y'

    let ctaTitle = ''

    const defaultCtaTitle = 'patient_detail:button_request_resupply'

    const normalizedOrderStatus = normalizePatientOrderStatus(
      patient.patientOrderStatus
    )

    switch (normalizedOrderStatus) {
      case PatientOrderStatus.FailedDelivery:
      case PatientOrderStatus.Expired:
      case PatientOrderStatus.Pending:
      case PatientOrderStatus.Delivered:
      case PatientOrderStatus.Shipped:
      case PatientOrderStatus.ReadyForResupply:
      case PatientOrderStatus.InTransit:
      case PatientOrderStatus.OutForDelivery:
      case PatientOrderStatus.Available_for_Pickup:
      case PatientOrderStatus.FailedAttempt:
        ctaTitle = defaultCtaTitle
        if (hasReconciliation) {
          ctaTitle =
            allStatusActions[ActionDropdownConstants.ReconcileVials].text
          setDetailsCtaButtonTitle(ctaTitle)
          return
        }
        if (
          normalizedOrderStatus === PatientOrderStatus.ReadyForResupply &&
          !stockOrderFlag
        ) {
          setDetailsCtaButtonTitle(defaultCtaTitle)
          return
        }
        break

      case PatientOrderStatus.BeginOrder:
      case PatientOrderStatus.AwaitingConsent:
      case PatientOrderStatus.AwaitingBaselineData:
        ctaTitle = allStatusActions[ActionDropdownConstants.BeginOrder].text
        break

      case PatientOrderStatus.IncompletePAF:
      case PatientOrderStatus.IncompleteCheckout:
        ctaTitle = allStatusActions[ActionDropdownConstants.ContinueOrder].text
        break

      case PatientOrderStatus.ProgramClosed:
      case PatientOrderStatus.PatientDiscontinued:
      case PatientOrderStatus.UnderMedicalReview:
        break
      default:
        break
    }
    setDetailsCtaButtonTitle(ctaTitle)
  }

  // CLOS-14590: the order-start check takes a second or two (Oracle, then Castor), so
  // the header button shows a spinner meanwhile. A start refused because Castor screening
  // is no longer complete links to the screening forms (same link as Step 2).
  const [isCheckingOrderStart, setIsCheckingOrderStart] = useState(false)
  const orderCheckOptions = (orderType?: string) => ({
    onCheckingChange: setIsCheckingOrderStart,
    refusalAction: screeningFormsRefusalAction(
      () =>
        openCastorEdcUrl(
          patient?.castorEdcUrl,
          user?.profile.email ?? userDetails?.contactCard.email
        ),
      orderType
    )
  })

  const getPatientProgramId = (selectedPatient?: PatientDetailsDto) => {
    const programId = selectedPatient?.program?.programId
    if (!programId) {
      dispatch(
        createAnnounceEvent(
          AnnounceMode.Error,
          t('patient_detail:missing_programme')
        )
      )
      return undefined
    }
    return programId
  }

  // Action Handlers Object
  const actionHandlers = {
    resupply: (patient: PatientDetailsDto) => {
      const programId = getPatientProgramId(patient)
      if (!programId) return

      if (isPtaOrderBlocked(patient)) {
        announcePtaOrderBlocked()
        return
      }

      void pushAccessFormWhenReady(
        history,
        dispatch,
        {
          pathname: `/programs/access-programs/${programId}/patient-access-form/${patient.currentPhysician.physicianId}`,
          state: {
            orderType: OPAOrderTypes.Resupply,
            patientNumber: patient.patientNumber,
            patientId: patient.patientId,
            patientStatus: patient.patientOrderStatus,
            stockOrder: patient.stockOrderFlag,
            from: window.location.pathname
          }
        },
        { onCheckingChange: setIsCheckingOrderStart }
      )
    },

    reconcileVials: (patient: PatientDetailsDto) => {
      const programId = getPatientProgramId(patient)
      if (!programId) return
      history.push(
        `/programs/my-physicians/${programId}/${patient.currentPhysician.physicianId}`,
        {
          from: window.location.pathname
        }
      )
    },

    trackOrder: (patient: PatientDetailsDto) => {
      if (patient.trackingLink) {
        window.location.href = patient.trackingLink
      }
    },

    transferPatient: (patient: PatientDetailsDto) => {
      if (!getPatientProgramId(patient)) return

      if (
        isPtaPatientTransferBlocked(
          patient.program?.status,
          program,
          defaultShippingAddress,
          portfolioCountryCode
        )
      ) {
        return
      }

      history.push(
        `/programs/my-physicians/${patient.currentPhysician.physicianId}/${patient.patientId}?transfer-patient-form=true`
      )
    },

    beginOrder: (patient: PatientDetailsDto) => {
      const programId = getPatientProgramId(patient)
      if (!programId) return

      const orderType =
        patient.stockOrderFlag === 'Y'
          ? OPAOrderTypes.StockOrder
          : OPAOrderTypes.InitialOrder

      if (isPtaOrderBlocked(patient)) {
        announcePtaOrderBlocked()
        return
      }

      // Check if order should be restricted
      if (shouldRestrictThisOrder(program, patient, orderType, dispatch)) {
        return
      }

      void pushAccessFormWhenReady(
        history,
        dispatch,
        {
          pathname: `/programs/access-programs/${programId}/patient-access-form/${patient.currentPhysician.physicianId}`,
          state: {
            orderType,
            patientNumber: patient.patientNumber,
            patientId: patient.patientId,
            patientStatus: patient.patientOrderStatus,
            stockOrder: patient.stockOrderFlag,
            from: window.location.pathname
          }
        },
        orderCheckOptions(orderType)
      )
    },

    continueOrder: (patient: PatientDetailsDto) => {
      const programId = getPatientProgramId(patient)
      if (!programId) return

      let orderType

      if (patient.patientOrderStatus === PatientOrderStatus.BeginOrder) {
        orderType = patient.stockOrderFlag
          ? OPAOrderTypes.StockOrder
          : OPAOrderTypes.InitialOrder
      } else {
        orderType = patient.orderType
      }

      // Check if order should be restricted
      if (
        shouldRestrictThisOrder(
          program,
          patient,
          orderType as OPAOrderTypes,
          dispatch
        )
      ) {
        return
      }

      void pushAccessFormWhenReady(
        history,
        dispatch,
        {
          pathname: `/programs/access-programs/${programId}/patient-access-form/${patient.currentPhysician.physicianId}`,
          state: {
            orderType,
            patientNumber: patient.patientNumber,
            patientId: patient.patientId,
            patientStatus: patient.patientOrderStatus,
            stockOrder: patient.stockOrderFlag,
            from: window.location.pathname
          }
        },
        orderCheckOptions(orderType)
      )
    },

    continueCheckout: async (patient: PatientDetailsDto) => {
      const programId = getPatientProgramId(patient)
      if (!programId) return

      // Only the Patients list passes the order number through router state;
      // deep links and refreshes recover it from the patient summary.
      let checkoutOrderNumber =
        orderNumber ||
        (await findPatientSummaryByNumber(patient.patientNumber)
          .then((summary) => summary?.orderNumber)
          .catch(() => undefined))

      if (!checkoutOrderNumber) {
        // CLOS-14565: the summary can omit the order number of a draft that
        // exists in EBS; look it up before telling the user it is missing.
        setIsPatientOrderNumber(true)
        setIsLookingUpDraftOrder(true)
        checkoutOrderNumber = await resolveCheckoutOrderNumber(
          undefined,
          patient.patientNumber,
          patient.program?.programId
        )
        setIsLookingUpDraftOrder(false)
        if (!checkoutOrderNumber) {
          return
        }
        setIsPatientOrderNumber(false)
      }

      // Check if order should be restricted
      if (
        shouldRestrictThisOrder(
          program,
          patient,
          patient.orderType as OPAOrderTypes,
          dispatch
        )
      ) {
        return
      }

      void pushCheckoutWhenReady(
        history,
        dispatch,
        {
          pathname: `/opa-checkout/${checkoutOrderNumber}`,
          state: {
            from: location?.state?.from,
            patientId: patient.patientId,
            patientNumber: patient.patientNumber,
            physicianId:
              physicianId ||
              (patient.currentPhysician?.physicianId
                ? String(patient.currentPhysician.physicianId)
                : undefined),
            orderType: patient.orderType,
            programId: programId,
            stockOrder: patient.stockOrderFlag
          } as LocationState
        },
        orderCheckOptions(patient.orderType)
      )
    }
  }

  const handlePrimaryCTA = () => {
    if (!patient) return

    if (!patientCentricFeatureFlag) {
      handleGoProgramDetail()
      return
    }

    const stockOrderFlag = patient?.stockOrderFlag === 'Y'
    const trackingLinkExists = patient?.trackingLink !== 'N/A'

    switch (patient.patientOrderStatus) {
      case PatientOrderStatus.FailedDelivery:
      case PatientOrderStatus.Expired:
      case PatientOrderStatus.Pending:
      case PatientOrderStatus.Delivered:
      case PatientOrderStatus.Shipped:
      case PatientOrderStatus.ReadyForResupply:
      case PatientOrderStatus.MedicalReviewDeclined:
      case PatientOrderStatus.HealthAuthorityDeclined:
        actionHandlers['resupply']?.(patient)
        if (
          patient.patientOrderStatus === PatientOrderStatus.Pending &&
          hasReconciliation
        ) {
          actionHandlers['reconcileVials']?.(patient)
          return
        }
        break
      case PatientOrderStatus.InTransit:
      case PatientOrderStatus.OutForDelivery:
      case PatientOrderStatus.Available_for_Pickup:
      case PatientOrderStatus.FailedAttempt:
        if (!stockOrderFlag) {
          actionHandlers['resupply']?.(patient)
          return
        }
        if (trackingLinkExists) {
          actionHandlers['trackOrder']?.(patient)
          return
        }
        break
      case PatientOrderStatus.Processing:
        actionHandlers['resupply']?.(patient)
        return
      case PatientOrderStatus.BeginOrder:
        actionHandlers['beginOrder']?.(patient)
        return
      case PatientOrderStatus.AwaitingBaselineData:
        // CLOS-13424 — notification / header Begin Order while status still
        // Awaiting Baseline Data after local getProgress confirmation.
        if (isConsentBaselineConfirmed) {
          actionHandlers['beginOrder']?.(patient)
        }
        return
      case PatientOrderStatus.IncompletePAF:
        actionHandlers['continueOrder']?.(patient)
        return
      case PatientOrderStatus.IncompleteCheckout:
        void actionHandlers['continueCheckout']?.(patient)
        return
      case PatientOrderStatus.PatientDiscontinued:
      default:
        handleGoProgramDetail()
        return
    }
  }

  const handleGoProgramDetail = () => {
    const programId = getPatientProgramId(patient)
    if (!programId) return
    history.push(`/programs/access-programs/${programId}`)
  }

  const handleFormSubmission = (form: ITransferForm) => {
    if (isPostTrialAccess) {
      return
    }

    const transferTo =
      form.destinationRadioId === '1' ? 'physician' : 'institute'
    history.push(
      `/programs/my-physicians/${physicianId}/${patientId}/transfer-patient/${transferTo}`
    )
  }

  const handleDestinationInputChange = (radioId: string) => {
    if (radioId === '1') {
      setIsTransferringAnotherInstitute(false)
      dispatch(clearAnnounceEvent())
      return
    }
    setIsTransferringAnotherInstitute(radioId === '2')
  }

  const handleHideTransferForm = () => {
    setIsFormOpen(false)
    setIsTransferringAnotherInstitute(false)
    dispatch(clearAnnounceEvent())
    history.push(`/programs/my-physicians/${physicianId}/${patientId}`)
  }

  const handleShowTransferForm = () => {
    if (isPostTrialAccess) {
      return
    }

    dispatch(clearAnnounceEvent()) // Hide any vial reconciliation
    setIsFormOpen(true)
    history.push(
      `/programs/my-physicians/${physicianId}/${patientId}?transfer-patient-form=true`
    )
  }

  const handleReferenceOrderClicked = (selectedOrderId: string | number) => {
    history.push(`/order/${selectedOrderId}`, {
      from: window.location.pathname
    })
  }

  const sortPastOrders = (
    pastOrders: IPastOrderSummary[],
    sortColumn: string,
    sortOrder: SortDirectionType = SortDirectionType.None
  ) => {
    if (!pastOrders || !sortColumn || sortOrder === SortDirectionType.None) {
      return pastOrders
    }

    return pastOrders.sort(
      (a: ShortOrderSummaryDto, b: ShortOrderSummaryDto) => {
        const valA = getValueByColumn(a, sortColumn)
        const valB = getValueByColumn(b, sortColumn)

        if (valA === valB) {
          const dateA = new Date(a.orderedDate)
          const dateB = new Date(b.orderedDate)
          return sortOrder === SortDirectionType.Ascending
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime()
        }

        if (typeof valA === 'string' && typeof valB === 'string') {
          return sortOrder === SortDirectionType.Ascending
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA)
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          return sortOrder === SortDirectionType.Ascending
            ? valA - valB
            : valB - valA
        } else {
          return 0
        }
      }
    )
  }

  const getValueByColumn = (
    order: ShortOrderSummaryDto,
    columnName: string
  ) => {
    switch (columnName) {
      case PastOrderColumn.OrderNumber:
        return order.orderNumber
      case PastOrderColumn.Medications:
        return order.medications[0].name.toLowerCase()
      case PastOrderColumn.OrderedDate:
        return order.orderedDate ? new Date(order.orderedDate).getTime() : 0
      case PastOrderColumn.OrderStatus:
        return order.orderStatus.toLowerCase()
      default:
        return null
    }
  }

  const handlePageSizeChange = (pageSize: number) => {
    const allPastOrdersClone = augmentedOrders.slice()

    const sortedPastOrders = sortPastOrders(
      allPastOrdersClone,
      pastOrderSortConfig.sortColumn as string,
      pastOrderSortConfig.sortOrder
    )

    if (sortedPastOrders) {
      setPastOrders([...sortedPastOrders.splice(0, pageSize)])

      setPagination({
        ...pagination,
        total: patient?.pastOrders.length ?? 0,
        take: pageSize,
        skip: 0
      })
    }
  }

  const handlePageClicked = (selectedPageIndex: number) => {
    const newSkip = (selectedPageIndex - 1) * pagination.take
    const allPastOrdersClone = augmentedOrders.slice()

    const sortedPastOrders = sortPastOrders(
      allPastOrdersClone,
      pastOrderSortConfig.sortColumn as string,
      pastOrderSortConfig.sortOrder
    )

    if (sortedPastOrders) {
      const newPastOrders = [
        ...sortedPastOrders.splice(newSkip, pagination.take)
      ]
      setPastOrders(newPastOrders)
      setPagination({
        ...pagination,
        skip: newSkip
      })
    }
  }

  /**
   * Trigger when clicking on a date selector show calendar
   * @param vial
   */
  const handleVialDateInputSelected = (vial: IPatientVial) => {
    // Find the order for this vial from augmentedOrders (which has all the processed data)
    const orderForVial = augmentedOrders.find(
      (o) => o.orderNumber.toString() === vial.orderNumber.toString()
    )

    // Debug logging removed

    // Work out minimum date: use vialCreationDate if available, otherwise orderedDate
    let minimumDate: string
    if (
      vial.vialCreationDate &&
      vial.vialCreationDate !== null &&
      vial.vialCreationDate !== ''
    ) {
      // Convert vialCreationDate from "dd-MMM-yy" format to ISO if needed
      try {
        // Try parsing as ISO first
        const parsedDate = DateTime.fromISO(vial.vialCreationDate)
        if (parsedDate.isValid) {
          minimumDate = vial.vialCreationDate
        } else {
          // If not ISO, try parsing as "dd-MMM-yy" format
          const parsedFromFormat = DateTime.fromFormat(
            vial.vialCreationDate,
            'dd-MMM-yy'
          )
          if (parsedFromFormat.isValid) {
            minimumDate = parsedFromFormat.toISODate()
          } else {
            // Fallback to orderedDate if parsing fails
            minimumDate = orderForVial?.orderedDate || new Date().toISOString()
          }
        }
      } catch {
        // Fallback to orderedDate if any error occurs
        minimumDate = orderForVial?.orderedDate || new Date().toISOString()
      }
    } else if (orderForVial?.orderedDate) {
      minimumDate = orderForVial.orderedDate
    } else {
      // Fallback to today if neither is available
      minimumDate = new Date().toISOString()
    }

    // Maximum date is always today
    const today = new Date().toISOString()

    // If no date has been administered yet, open calendar to the minimum date month
    // Otherwise open to the administered date
    const selectedDay = vial.dateAdministered
      ? convertDateStringToDay(vial.dateAdministered)
      : convertDateStringToDay(minimumDate)

    // Select the vial row (so we can update the correct one later)
    setSelectedVialRow(vial)
    // Show calendar
    setShowCalendar({
      minimumDate: convertDateStringToDay(minimumDate),
      selectedDay: selectedDay,
      maximumDate: convertDateStringToDay(today),
      isOpen: true
    })
  }

  /**
   * When day is selected in calendar update the nested vial row date
   * @param selectedDay
   */
  const handleDaySelected = (selectedDay: Day) => {
    // Find matching order and vial
    const matchingOrderIndex = pastOrders.findIndex(
      (ord) =>
        ord.orderNumber.toString() === selectedVialRow?.orderNumber.toString()
    )
    const matchingOrder = pastOrders[matchingOrderIndex]
    const matchingVialIndex = matchingOrder.vialsToReconcile.findIndex(
      (v) => v.vialNumber === selectedVialRow?.vialNumber
    )
    const vialToUpdate = matchingOrder.vialsToReconcile[matchingVialIndex]
    const isoDate = convertDayToISODateString(selectedDay)
    const updatedVial = {
      ...vialToUpdate,
      dateAdministered: isoDate,
      dateAdministeredFormatted: DateTime.fromISO(isoDate)
        .setLocale(getBrowserLocale())
        .toLocaleString(DateTime.DATE_SHORT)
    }
    // Update all the orders with the updated vial
    const updatedPastOrders = {
      ...pastOrders
    }
    // Use lodash to update the nested vial
    set(
      updatedPastOrders,
      `[${matchingOrderIndex}].vialsToReconcile[${matchingVialIndex}]`,
      updatedVial
    )
    // Deselect the row
    setSelectedVialRow(undefined)
    // Close calendar
    setShowCalendar({
      ...showCalendar,
      minimumDate: matchingOrder.dayOrderShipped,
      selectedDay: selectedDay,
      isOpen: false
    })
  }

  const handleSaveChangesToVial = (updatedVial: IPatientVial) => {
    const { dateAdministered, vialNumber, vialStatus, batchNumber } =
      updatedVial
    setIsSavingVial(true)
    setSelectedVialRow(updatedVial)
    patient?.currentPhysician &&
      reconcileVialForPatient(
        patient.currentPhysician.physicianId.toString(),
        patient.patientId.toString(),
        {
          dateAdministered,
          vialNumber,
          vialStatus,
          batchNumber
        }
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            // Find matching order and vial
            const matchingOrderIndex = pastOrders.findIndex(
              (ord) =>
                ord?.orderNumber?.toString() ===
                updatedVial?.orderNumber?.toString()
            )
            const matchingOrder = pastOrders[matchingOrderIndex]
            const matchingVialIndex = matchingOrder.vialsToReconcile.findIndex(
              (v) =>
                v?.vialNumber?.toString() ===
                updatedVial?.vialNumber?.toString()
            )
            // Use lodash to update the nested vial that was just reconciled
            const updatedOrders = set(
              pastOrders,
              `[${matchingOrderIndex}].vialsToReconcile[${matchingVialIndex}]`,
              {
                ...updatedVial,
                wasReconciled: true
              }
            )

            setPastOrders(updatedOrders)

            // Update vials to reconcile counter
            const totalVialsToReconcile =
              getNumberOfVialsToReconcile(updatedOrders)

            setVialsToReconcileCounter(totalVialsToReconcile)

            analyticsServiceSingleton.trackEvent(AnalyticsEvent.ReconcileVial, {
              patientId,
              programId: program?.program?.cliniportProgramId,
              programName: program?.programName
            })

            // Should we scroll to top and show vial reconciliation complete message?
            if (totalVialsToReconcile === 0) {
              window.scrollTo({
                behavior: 'smooth',
                top: 0
              })
              dispatch(
                createAnnounceEvent(
                  AnnounceMode.Success,
                  `All products have been reconciled.`,
                  'Complete'
                )
              )
            }
          }
        })
        .catch((error) => {
          window.scrollTo(0, 0)
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error reconciling the vial. ${error}`
            )
          )
        })
        .finally(() => {
          setIsSavingVial(false)
          setSelectedVialRow(undefined)
        })
  }

  const handleDiscontinueFromProgram = (): void => {
    if (
      vialsToReconcileCounter &&
      vialsToReconcileCounter > 0 &&
      hasReconciliation
    ) {
      setIsReconcileWarningOpen(true)
    } else {
      handleOpenConfirmation()
    }
  }

  const handleCloseReconcileWarningModal = (): void => {
    setIsReconcileWarningOpen(false)
  }

  const handleBackToPatientDetails = () => {
    handleClearSubmission()
    getPatient()
  }

  const handleDownload = (documentItem: ProgramDocumentDto) => {
    documentItem.generatedId &&
      downloadDocumentById(documentItem.generatedId.toString())
        .then((response) => {
          documentItem.documentTypeName &&
            documentItem.documentUrl &&
            download(
              response.data,
              getFilenameFromUrl(documentItem.documentUrl),
              documentItem.documentTypeName
            )
          analyticsServiceSingleton.trackEvent(
            AnalyticsEvent.DocumentDownload,
            {
              ...documentItem,
              patientId
            }
          )
        })
        .catch(() => {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              t('order_detail:error_downloading_invoice')
            )
          )
        })
  }

  const goToAdverseEventPage = () => {
    history.push('/products/adverse-event')
  }

  const handleAdverseEventDownload = () => {
    if (program?.programDocuments && program.programDocuments.length > 0) {
      const adverseEventDocument: ProgramDocumentDto | undefined =
        program.programDocuments.find(
          (document: ProgramDocumentDto) =>
            document.documentTypeCode?.toUpperCase() ===
            PatientDocumentType.ADVERSE_EVENT_FORM.toUpperCase()
        )
      if (adverseEventDocument) {
        handleDownload(adverseEventDocument)
      } else {
        goToAdverseEventPage()
      }
    } else {
      goToAdverseEventPage()
    }
  }

  const handleOpenPatientAccessForm = (
    orderNumber?: number,
    opaAccessToken?: string
  ) => {
    if (!orderNumber || !opaAccessToken) {
      // Fall back to behavior where we show an error
      handlePatientAccessFormError()
      return
    }

    const programId = getPatientProgramId(patient)
    if (!programId) return

    const orderNumberStr = orderNumber.toString()

    // Check if we already have the URL cached
    if (orderOpaUrls[orderNumberStr]) {
      history.push(
        {
          pathname: `/programs/access-programs/${programId}/patient-access-form/${physicianId}`
        },
        {
          endpoint: orderOpaUrls[orderNumberStr],
          from: window.location.pathname
        }
      )
      return
    }

    // Otherwise, fetch the URL for this specific order
    setIsLoadingOrderOpaUrl(orderNumberStr)
    getReadOnlyOpaUrl(orderNumber, { opaAccessToken })
      .then((response) => {
        if (response.data.endPoint) {
          // Cache the URL for future use
          const endpointUrl: string = response.data.endPoint
          setOrderOpaUrls((prev) => {
            const newUrls = { ...prev }
            newUrls[orderNumberStr] = endpointUrl
            return newUrls
          })

          // Navigate to the form
          history.push(
            {
              pathname: `/programs/access-programs/${programId}/patient-access-form/${physicianId}`
            },
            {
              endpoint: endpointUrl,
              from: window.location.pathname
            }
          )
        }
      })
      .catch((error) => {
        dispatch(
          createAnnounceEvent(
            AnnounceMode.Error,
            `There was an error retrieving the patient access form. ${error}`
          )
        )
      })
      .finally(() => {
        setIsLoadingOrderOpaUrl(null)
      })
  }

  const handlePatientAccessFormError = () => {
    history.push('/about/contact-us')
  }

  const mockRedirect = () => {
    history.push(
      `/programs/my-physicians/${physicianId}/${patientId}/discontinue-patient`
    )
  }

  //endregion
  //region useEffects

  type Deps = [boolean, PatientDetailsDto, IRestEndpointState<InstituteDto>]
  useEffectOnlyOnce(
    (dependencies: Deps) => {
      analyticsServiceSingleton.trackPageView(AnalyticsPageEvent.ViewPatient, {
        'Physician ID': dependencies[1].currentPhysician.physicianId,
        'Patient ID': dependencies[1].patientId,
        'Institute ID': dependencies[2].data?.instituteId
      })
    },
    [isLoading || isProgramLoading, patient, institute],
    (dependencies: Deps) =>
      dependencies[0] === false && dependencies[1] && dependencies[2]
  )

  const handleChangeTab = (tabIndex: number) => {
    setTabIndex(tabIndex)
    window.scrollTo({
      behavior: 'smooth',
      top: 0
    })
  }

  const ordersTabIndex = isPostTrialAccess ? 1 : 0
  const recordTreatmentTabIndex = isPostTrialAccess ? 2 : 1

  const tabData: TabModel[] = [
    ...(isPostTrialAccess
      ? [
          {
            linkText: t('patient_detail:tab_title_consent_baseline'),
            showCountNumber: false,
            countNumber: 0,
            tabIndex: 0,
            onClick: handleChangeTab
          }
        ]
      : []),
    {
      linkText: t('patient_detail:tab_title_one_past_orders'),
      showCountNumber: true,
      countNumber: augmentedOrders.length,
      tabIndex: ordersTabIndex,
      onClick: handleChangeTab
    },
    ...(showRecordTreatmentTab
      ? [
          {
            linkText: t('patient_detail:tab_title_two_past_orders'),
            showCountNumber: true,
            get countNumber() {
              const pastOrdersFiltered = pastOrders.filter(
                (o) => o.canBeReconciled
              )
              return pastOrdersFiltered.reduce((totalCount, order) => {
                return totalCount + order.vialsToReconcile.length
              }, 0)
            },
            tabIndex: recordTreatmentTabIndex,
            onClick: handleChangeTab
          }
        ]
      : [])
  ]

  const pastOrderColumns: IPastOrderColumn[] = [
    {
      title: t('patient_detail:past_orders.order_id_column'),
      renderValue: function column(order: ShortOrderSummaryDto) {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.heading[0]}
            fontWeight={ClinTheme.fontWeights.medium}
            color={ClinTheme.colors.tealishBlue}
          >
            {order.orderNumber}
          </ClinText>
        )
      },
      width: '20%',
      isSortable: true,
      name: PastOrderColumn.OrderNumber
    },
    {
      title: t('patient_detail:past_orders.products_column'),
      renderValue: function column(order: ShortOrderSummaryDto) {
        const medicationName = order.medications[0].name

        return (
          <Tooltip
            theme="light"
            position="bottom"
            trigger="mouseenter"
            arrow={true}
            title={medicationName}
          >
            <ClinText
              whiteSpace="nowrap"
              fontSize={ClinTheme.fontSizes[1]}
              lineHeight={ClinTheme.lineHeights.heading[0]}
              width="140px"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {medicationName}
            </ClinText>
          </Tooltip>
        )
      },
      width: '25%',
      isSortable: true,
      name: PastOrderColumn.Medications
    },
    {
      title: t('patient_detail:past_orders.date_placed_column'),
      renderValue: function column(order: ShortOrderSummaryDto) {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.heading[0]}
            color="#4F5A65"
          >
            {order.orderedDate
              ? DateTime.fromISO(order.orderedDate)
                  .toFormat('dd/MMM/yyyy')
                  .toUpperCase()
              : ''}
          </ClinText>
        )
      },
      width: '20%',
      isSortable: true,
      name: PastOrderColumn.OrderedDate
    },
    {
      title: t('patient_detail:past_orders.status_column'),
      renderValue: function column(order: ShortOrderSummaryDto) {
        const validPatientStatuses = [
          PatientOrderStatus.Submitted,
          PatientOrderStatus.UnderMedicalReview,
          PatientOrderStatus.MedicalReviewDeclined,
          PatientOrderStatus.HealthAuthorityReview,
          PatientOrderStatus.HealthAuthorityDeclined
        ]
        order.orderStatus =
          order.orderStatus === OrderStatus.On_Hold
            ? validPatientStatuses.includes(
                patient?.patientOrderStatus as PatientOrderStatus
              )
              ? (patient?.patientOrderStatus as PatientOrderStatus)
              : PatientOrderStatus.UnderReview
            : order.orderStatus

        let statusComponent
        switch (order.orderStatus) {
          case PatientOrderStatus.Processing:
          case PatientOrderStatus.Submitted:
          case PatientOrderStatus.Shipped:
            statusComponent = (
              <StatusTooltip
                tooltipText={t(
                  patientOrderStatusTooltipTranslation[order.orderStatus]
                )}
              >
                <StyledStatusWrapper
                  color="rgba(0, 162, 35, 0.1)"
                  style={{ width: '90%' }}
                >
                  <ClinText
                    whiteSpace="nowrap"
                    fontWeight={ClinTheme.fontWeights.medium}
                    color={ClinTheme.colors.greenValid}
                    textAlign="center"
                    fontSize={ClinTheme.fontSizes[0]}
                    lineHeight="2"
                  >
                    {t(patientOrderStatusTranslation[order.orderStatus])}
                  </ClinText>
                </StyledStatusWrapper>
              </StatusTooltip>
            )
            break
          case PatientOrderStatus.InTransit:
          case PatientOrderStatus.OutForDelivery:
          case PatientOrderStatus.Available_for_Pickup:
          case PatientOrderStatus.FailedAttempt:
            statusComponent = (
              <StatusTooltip
                tooltipText={t(
                  patientOrderStatusTooltipTranslation[order.orderStatus]
                )}
              >
                <StyledStatusWrapper
                  color="rgba(0, 162, 35, 0.1)"
                  style={{ width: '90%' }}
                >
                  <ClinText
                    whiteSpace="nowrap"
                    fontWeight={ClinTheme.fontWeights.medium}
                    color={ClinTheme.colors.greenValid}
                    textAlign="center"
                    fontSize={ClinTheme.fontSizes[0]}
                    lineHeight="2"
                  >
                    {t(patientOrderStatusTranslation[order.orderStatus])}
                    {patient?.trackingLink! !== 'N/A' && (
                      <StyledTrackingLinkWrapper
                        href={patient?.trackingLink!}
                        text={t('patient_detail:patient_actions.track')}
                        target="_blank"
                      />
                    )}
                  </ClinText>
                </StyledStatusWrapper>
              </StatusTooltip>
            )
            break
          case PatientOrderStatus.Delivered:
            statusComponent = (
              <StyledStatusWrapper
                color="rgba(0, 162, 35, 0.1)"
                style={{ width: '90%' }}
              >
                <ClinText
                  whiteSpace="nowrap"
                  fontWeight={ClinTheme.fontWeights.medium}
                  color={ClinTheme.colors.greenValid}
                  textAlign="center"
                  fontSize={ClinTheme.fontSizes[0]}
                  lineHeight="2"
                >
                  {t(patientOrderStatusTranslation[order.orderStatus])}
                </ClinText>
              </StyledStatusWrapper>
            )
            break
          case PatientOrderStatus.UnderMedicalReview:
          case PatientOrderStatus.UnderReview:
          case PatientOrderStatus.HealthAuthorityReview:
            statusComponent = (
              <StatusTooltip
                tooltipText={t(
                  patientOrderStatusTooltipTranslation[order.orderStatus]
                )}
              >
                <StyledStatusWrapper
                  color="rgba(215, 106, 0, 0.10)"
                  style={{ width: '90%' }}
                >
                  <ClinText
                    whiteSpace="nowrap"
                    fontWeight={ClinTheme.fontWeights.medium}
                    color={ClinTheme.colors.orangeWarn}
                    textAlign="center"
                    fontSize={ClinTheme.fontSizes[0]}
                    lineHeight="2"
                  >
                    {t(patientOrderStatusTranslation[order.orderStatus])}
                  </ClinText>
                </StyledStatusWrapper>
              </StatusTooltip>
            )
            break
          default:
            statusComponent = (
              <StyledStatusWrapper
                color="rgba(117, 117, 117, 0.1)"
                style={{ width: '90%' }}
              >
                <ClinText
                  whiteSpace="nowrap"
                  fontWeight={ClinTheme.fontWeights.medium}
                  color={ClinTheme.colors.darkGrey}
                  textAlign="center"
                  fontSize={ClinTheme.fontSizes[0]}
                  lineHeight="2"
                >
                  {order.orderStatus}
                </ClinText>
              </StyledStatusWrapper>
            )
            break
        }

        return statusComponent
      },
      width: '25%',
      isSortable: true,
      name: PastOrderColumn.OrderStatus
    },
    {
      title: t('patient_detail:past_orders.patient_access_form_column'),
      renderValue: function column(order: ShortOrderSummaryDto) {
        const isLoadingThisOrder =
          isLoadingOrderOpaUrl === order.orderNumber.toString()
        const hasToken = order.opaAccessToken && order.opaAccessToken.length > 0

        return (
          <PatientAccessFormItem
            onClick={(e) => {
              e.stopPropagation()
              if (isLoadingThisOrder) {
                return // Prevent multiple clicks while loading
              }

              if (hasToken) {
                handleOpenPatientAccessForm(
                  order.orderNumber,
                  order.opaAccessToken
                )
              } else {
                handlePatientAccessFormError()
              }
            }}
          >
            <StyledDocumentIcon
              iconName={ClinIconPathName.Document}
              iconFill={ClinTheme.colors.tealishBlue}
            />
            <ClinText
              marginTop={2}
              fontWeight={ClinTheme.fontWeights.bold}
              color={ClinTheme.colors.tealishBlue}
            >
              {isLoadingThisOrder ? (
                <ClinSpinner size={ClinTheme.space[3]} />
              ) : hasToken ? (
                t('common:buttons.view')
              ) : (
                t('patient_detail:paf_error')
              )}
            </ClinText>
          </PatientAccessFormItem>
        )
      },
      width: '10%',
      isSortable: false,
      name: PastOrderColumn.PatientAccessForm
    }
  ]

  const handleSortConfigChange = (
    sortColumn: string,
    sortOrder: SortDirectionType
  ) => {
    setPastOrderSortConfig({ sortColumn, sortOrder })
  }

  //endregion
  if (!isLoading && patient && !patient.program?.programId) {
    return (
      <>
        <ClinAnnounceBar mode={AnnounceMode.Error}>
          {t('patient_detail:missing_programme')}
        </ClinAnnounceBar>
        <ClinButton
          onClick={() => history.push(`/programs/my-physicians/${physicianId}`)}
        >
          {t('patient_detail:button_back_to_physician')}
        </ClinButton>
      </>
    )
  }
  return (
    <>
      {!isConfirmationSubmitted ? (
        <>
          <DiscontinueWarningModal
            isOpen={isReconcileWarningOpen}
            handleClose={handleCloseReconcileWarningModal}
          />
          <DiscontinueConfirmationModal
            isOpen={isConfirmationOpen}
            isSubmitting={isConfirmationSubmitting}
            hasError={confirmationError?.isAxiosError}
            errorMessage={confirmationError?.message}
            handleSubmit={mockRedirect}
            handleClose={handleCloseConfirmation}
          />
          {showConsentBaselineCompleteNotification && (
            // The portal's success notification in its action look (Figma toast/snackbar):
            // same place and auto-hide as the other success messages, plus Begin order.
            <ClinNotification
              title={PTA_BASELINE_CAPTURED_SUCCESS_MESSAGE}
              action={{
                label: PTA_BEGIN_ORDER_ACTION_LABEL,
                onClick: handlePrimaryCTA
              }}
              onClose={dismissConsentBaselineCompleteNotification}
              style={{ maxWidth: PTA_SUCCESS_NOTIFICATION_MAX_WIDTH }}
            />
          )}
          <PatientDetail
            isLoading={Boolean(!patient && (isLoading || isProgramLoading))}
            detailsCtaButtonTitle={detailsCtaButtonTitle}
            isCheckingOrderStart={isCheckingOrderStart}
            isProgramClosed={isProgramClosed}
            allowsDiscontinuation={allowsDiscontinuation}
            programCatalog={program}
            defaultShippingAddress={defaultShippingAddress}
            instituteCountryCode={portfolioCountryCode}
            patient={patient}
            pastOrders={pastOrders}
            selectedOrdersTabIndex={tabIndex}
            selectedVialRow={selectedVialRow}
            tabData={tabData}
            pastOrderColumns={pastOrderColumns}
            handleChangeTab={handleChangeTab}
            isFormOpen={isFormOpen}
            disableTransferButton={
              isTransferringAnotherInstitute && vialsToReconcileCounter > 0
            }
            vialsUnitType={program?.program?.unitType ?? null}
            vialReconciliationType={$enum(
              VialReconciliationType
            ).asValueOrDefault(
              program?.program?.reconciliationType,
              VialReconciliationType.DispositionBatchAndDate
            )}
            programRealWorldData={getShowRWDCard(program)}
            handleSortConfigChange={handleSortConfigChange}
            handleHideTransferForm={handleHideTransferForm}
            handleFormSubmission={handleFormSubmission}
            handleDestinationInputChange={handleDestinationInputChange}
            handlePrimaryCTA={handlePrimaryCTA}
            handleReferenceOrderClicked={handleReferenceOrderClicked}
            pagination={pagination}
            handlePageClicked={handlePageClicked}
            handlePageSizeChange={handlePageSizeChange}
            isSavingVial={isSavingVial}
            handleSaveChangesToVial={handleSaveChangesToVial}
            handleVialDateInputSelected={handleVialDateInputSelected}
            handleDiscontinueFromProgram={handleDiscontinueFromProgram}
            handleShowTransferForm={handleShowTransferForm}
            showRecordTreatmentTab={showRecordTreatmentTab}
            showConsentBaselineTab={isPostTrialAccess}
            castorEdcUrl={patient?.castorEdcUrl ?? undefined}
            onConfirmConsentBaseline={handleConfirmConsentBaseline}
            isConfirmingConsentBaseline={isCheckingConsentBaselineProgress}
            isConsentBaselineConfirmed={isConsentBaselineConfirmed}
            baselineErrorMessage={baselineErrorMessage}
            onDismissBaselineError={dismissBaselineError}
          />
          <StyledCalendarModal
            isOpen={showCalendar.isOpen}
            isDismissible={true}
            maxWidth={'none'}
            removePadding={true}
            onClose={() => setShowCalendar({ ...showCalendar, isOpen: false })}
          >
            <StyledCalendarWrapper>
              <Calendar
                value={showCalendar.selectedDay}
                minimumDate={showCalendar.minimumDate}
                maximumDate={showCalendar.maximumDate}
                colorPrimary={ClinTheme.colors.primaryMid}
                colorPrimaryLight={ClinTheme.colors.primaryLight}
                onChange={handleDaySelected}
              />
              <StyledCalendarModal
                isOpen={showCalendar.isOpen}
                isDismissible={true}
                maxWidth={'none'}
                removePadding={true}
                onClose={() =>
                  setShowCalendar({ ...showCalendar, isOpen: false })
                }
              >
                <StyledCalendarWrapper>
                  <Calendar
                    value={showCalendar.selectedDay}
                    minimumDate={showCalendar.minimumDate}
                    maximumDate={showCalendar.maximumDate}
                    colorPrimary={ClinTheme.colors.primaryMid}
                    colorPrimaryLight={ClinTheme.colors.primaryLight}
                    onChange={handleDaySelected}
                  />
                </StyledCalendarWrapper>
              </StyledCalendarModal>
            </StyledCalendarWrapper>
          </StyledCalendarModal>
          <MissingDraftOrderModal
            isOpen={isPatientOrderNumber}
            isLookingUp={isLookingUpDraftOrder}
            physicianId={physicianId}
            patientId={patientId}
            onClose={() => setIsPatientOrderNumber(false)}
            onStartedAgain={() => {
              setIsPatientOrderNumber(false)
              getPatient()
            }}
          />
        </>
      ) : (
        <>
          {patient?.patientNumber && (
            <PatientDetailDiscontinued
              patientNumber={patient.patientNumber}
              handleAdverseEventDownload={handleAdverseEventDownload}
              handleBackToPatientDetails={handleBackToPatientDetails}
            />
          )}
        </>
      )}
    </>
  )
}
